import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { orderIssueDetailTooltip, orderIssueMeta } from "./orderIssueUx";

/**
 * Monospace issue code with a rich hover/focus tooltip (portal — not clipped).
 */
export function IssueCodeChip({ code, lang = "en" }) {
  const key = String(code || "").trim();
  const wrapRef = useRef(null);
  const leaveTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const el = wrapRef.current;
    if (!el || typeof window === "undefined") return;
    const rect = el.getBoundingClientRect();
    const maxW = Math.min(320, window.innerWidth - 24);
    let left = rect.left;
    if (left + maxW > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - maxW - 12);
    }
    const spaceBelow = window.innerHeight - rect.bottom;
    const preferDown = spaceBelow > 160 || rect.top < 160;
    setPos({
      left,
      top: preferDown ? rect.bottom + 6 : undefined,
      bottom: preferDown ? undefined : window.innerHeight - rect.top + 6,
      maxW,
      preferDown,
    });
  }, []);

  const onEnter = () => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    place();
    setOpen(true);
  };
  const onLeave = () => {
    leaveTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, place]);

  useEffect(
    () => () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    },
    []
  );

  if (!key) return null;
  const meta = orderIssueMeta(key);
  const body = orderIssueDetailTooltip(key, lang);
  const gr = lang === "gr";

  return (
    <>
      <span
        ref={wrapRef}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={onEnter}
        onBlur={onLeave}
        tabIndex={0}
        aria-describedby={open ? `issue-tip-${key}` : undefined}
        style={{
          display: "inline-block",
          fontSize: 11,
          fontWeight: 700,
          color: "var(--text-muted)",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          marginTop: 2,
          cursor: "help",
          borderBottom: "1px dotted var(--text-muted)",
          outline: "none",
        }}
        title="" // prefer rich tooltip; keep native empty to avoid double tip
      >
        {key}
      </span>
      {open &&
        pos &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            id={`issue-tip-${key}`}
            role="tooltip"
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              bottom: pos.bottom,
              width: pos.maxW,
              zIndex: 10050,
              padding: "10px 12px",
              borderRadius: 10,
              background: "#1f2937",
              color: "#f9fafb",
              fontSize: 12,
              lineHeight: 1.45,
              boxShadow: "0 8px 24px rgba(0,0,0,0.28)",
              pointerEvents: "auto",
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 4 }}>
              {meta?.title || key}
            </div>
            <div
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 11,
                opacity: 0.75,
                marginBottom: 8,
              }}
            >
              {key}
            </div>
            <div style={{ whiteSpace: "pre-wrap" }}>{body}</div>
            <div style={{ marginTop: 8, fontSize: 10, opacity: 0.55 }}>
              {gr ? "Κωδικός drop / eligibility" : "Drop / eligibility code"}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
